import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { adminAPI, downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Users, FileText, BarChart3, Download, TrendingUp, Moon, Eye, Calendar, User, Trash2, AlertTriangle, ChevronDown, ChevronRight, FolderOpen, Folder } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [userEntries, setUserEntries] = useState([]);
  const [loadingUserEntries, setLoadingUserEntries] = useState(false);
  
  // Delete confirmation state
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Expanded patients state for folder view
  const [expandedPatients, setExpandedPatients] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, entriesRes, statsRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getAllEntries(),
        adminAPI.getStatistics()
      ]);
      setUsers(usersRes.data);
      setEntries(entriesRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Direct download - opens URL in new tab to trigger browser download
  const handleExportAllCSV = () => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/admin/export/csv?token=${token}`;
    window.open(url, '_blank');
    toast.success("Download started - check your Downloads folder");
  };

  const handleDownloadMasterReport = () => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/admin/master-report?token=${token}`;
    window.open(url, '_blank');
    toast.success("Master Report download started - check your Downloads folder");
  };

  const handleDownloadEntry = (entryId, subjectName, date) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/admin/entry/${entryId}/download-pdf?token=${token}`;
    window.open(url, '_blank');
    toast.success(`Downloading PDF report for ${subjectName}`);
  };

  const handleDownloadUserEntries = (userId, userName) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/admin/user/${userId}/download?token=${token}`;
    window.open(url, '_blank');
    toast.success(`Downloading CSV for ${userName}`);
  };

  const handleDownloadUserPdfReport = (userId, userName, patientId) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/admin/user/${userId}/download-pdf?token=${token}`;
    window.open(url, '_blank');
    toast.success(`Downloading comprehensive PDF report for ${userName}`);
  };

  const handleViewUserEntries = async (userToView) => {
    setSelectedUser(userToView);
    setLoadingUserEntries(true);
    try {
      const response = await adminAPI.getUserEntries(userToView.id);
      setUserEntries(response.data);
    } catch (error) {
      toast.error("Failed to load user entries");
      setUserEntries([]);
    } finally {
      setLoadingUserEntries(false);
    }
  };

  const handleViewEntryDetails = (entry) => {
    setSelectedEntry(entry);
  };

  // Delete entry handlers
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete) return;
    
    setIsDeleting(true);
    try {
      await adminAPI.deleteEntry(entryToDelete.id);
      toast.success(`Entry for ${entryToDelete.subject_name} on ${entryToDelete.date} deleted successfully`);
      
      // Refresh data to update statistics and entries list
      await loadData();
      
      // Close the confirmation dialog
      setEntryToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete entry");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setEntryToDelete(null);
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case "Good": return "bg-green-100 text-green-800 border-green-200";
      case "Moderate": return "bg-amber-100 text-amber-800 border-amber-200";
      case "Poor": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-slate-100 text-slate-800";
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getUserEntryCount = (userId) => {
    return entries.filter(e => e.user_id === userId).length;
  };

  const getUserLatestEntry = (userId) => {
    const userEntries = entries.filter(e => e.user_id === userId);
    return userEntries.length > 0 ? userEntries[0] : null;
  };

  // Group entries by patient for folder view
  const getEntriesGroupedByPatient = () => {
    const grouped = {};
    entries.forEach(entry => {
      const patientKey = entry.user_id || entry.subject_name;
      if (!grouped[patientKey]) {
        // Find the user info
        const userInfo = users.find(u => u.id === entry.user_id);
        grouped[patientKey] = {
          patientId: userInfo?.patient_id || 'N/A',
          patientName: entry.subject_name,
          email: userInfo?.email || 'N/A',
          userId: entry.user_id,
          entries: []
        };
      }
      grouped[patientKey].entries.push(entry);
    });
    // Sort entries within each patient by date (newest first)
    Object.values(grouped).forEach(patient => {
      patient.entries.sort((a, b) => new Date(b.date) - new Date(a.date));
    });
    return Object.values(grouped).sort((a, b) => a.patientName.localeCompare(b.patientName));
  };

  const togglePatientExpanded = (patientKey) => {
    setExpandedPatients(prev => ({
      ...prev,
      [patientKey]: !prev[patientKey]
    }));
  };

  const expandAllPatients = () => {
    const allExpanded = {};
    getEntriesGroupedByPatient().forEach(patient => {
      allExpanded[patient.userId || patient.patientName] = true;
    });
    setExpandedPatients(allExpanded);
  };

  const collapseAllPatients = () => {
    setExpandedPatients({});
  };

  const classificationData = statistics ? [
    { name: "Good", value: statistics.classification_breakdown.Good || 0, color: "#22c55e" },
    { name: "Moderate", value: statistics.classification_breakdown.Moderate || 0, color: "#f59e0b" },
    { name: "Poor", value: statistics.classification_breakdown.Poor || 0, color: "#ef4444" }
  ] : [];

  const last7DaysData = entries.slice(0, 7).reverse().map(entry => ({
    date: entry.date.split('-').slice(1).join('/'),
    efficiency: entry.sleep_efficiency
  }));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <img src={LOGO_URL} alt="SleepMediary" className="h-10 w-auto" />
                <span className="font-heading font-bold text-xl text-slate-900">SleepMediary</span>
              </a>
              <Badge className="bg-primary/10 text-primary border-0">Admin</Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">
                <span className="font-medium text-slate-900">{user?.name}</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600 mt-1">Manage users and view sleep data analytics</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleDownloadMasterReport}
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
              data-testid="master-report-btn"
            >
              <FileText className="h-5 w-5 mr-2" />
              Download Master Report
            </Button>
            <Button 
              onClick={handleExportAllCSV}
              className="btn-primary"
              data-testid="export-csv-btn"
            >
              <Download className="h-5 w-5 mr-2" />
              Export All CSV
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-white rounded-xl skeleton"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="card-metric">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{statistics?.total_users || 0}</p>
              </div>
            </Card>
            
            <Card className="card-metric">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Entries</p>
                <p className="text-2xl font-bold text-green-600">{statistics?.total_entries || 0}</p>
              </div>
            </Card>
            
            <Card className="card-metric">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg Efficiency</p>
                <p className="text-2xl font-bold text-amber-600">{statistics?.avg_sleep_efficiency || 0}%</p>
              </div>
            </Card>
            
            <Card className="card-metric">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Moon className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Good Sleep</p>
                <p className="text-2xl font-bold text-slate-900">
                  {statistics?.classification_breakdown?.Good || 0}
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="patients" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <FolderOpen className="h-4 w-4 mr-2" />
              Patient Folders
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              All Patients
            </TabsTrigger>
            <TabsTrigger value="entries" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              All Entries
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-lg">Sleep Classification Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {classificationData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={classificationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {classificationData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-500">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Sleep Efficiency</CardTitle>
                </CardHeader>
                <CardContent>
                  {last7DaysData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={last7DaysData}>
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Efficiency']} />
                        <Bar dataKey="efficiency" fill="#2563EB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-500">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Patient Folders Tab - Grouped by Patient */}
          <TabsContent value="patients">
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Patient Folders</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={expandAllPatients}>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Expand All
                    </Button>
                    <Button variant="outline" size="sm" onClick={collapseAllPatients}>
                      <ChevronRight className="h-4 w-4 mr-1" />
                      Collapse All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {getEntriesGroupedByPatient().length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FolderOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No patient entries found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getEntriesGroupedByPatient().map((patient) => {
                      const patientKey = patient.userId || patient.patientName;
                      const isExpanded = expandedPatients[patientKey];
                      const avgEfficiency = patient.entries.length > 0 
                        ? Math.round(patient.entries.reduce((sum, e) => sum + e.sleep_efficiency, 0) / patient.entries.length)
                        : 0;
                      
                      return (
                        <div key={patientKey} className="border border-slate-200 rounded-lg overflow-hidden">
                          {/* Patient Folder Header */}
                          <div 
                            className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => togglePatientExpanded(patientKey)}
                            data-testid={`patient-folder-${patientKey}`}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-slate-500" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-slate-500" />
                              )}
                              {isExpanded ? (
                                <FolderOpen className="h-5 w-5 text-primary" />
                              ) : (
                                <Folder className="h-5 w-5 text-primary" />
                              )}
                              <div>
                                <p className="font-semibold text-slate-900">{patient.patientName}</p>
                                <p className="text-sm text-slate-500">
                                  ID: {patient.patientId} • {patient.email}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-slate-500">Entries</p>
                                <p className="font-semibold text-slate-900">{patient.entries.length}/14</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-slate-500">Avg Efficiency</p>
                                <p className="font-semibold text-slate-900">{avgEfficiency}%</p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadUserPdfReport(patient.userId, patient.patientName, patient.patientId);
                                }}
                                disabled={patient.entries.length === 0}
                                className="border-primary text-primary hover:bg-primary/10"
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                PDF
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadUserEntries(patient.userId, patient.patientName);
                                }}
                                disabled={patient.entries.length === 0}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                CSV
                              </Button>
                            </div>
                          </div>
                          
                          {/* Patient Entries - Expandable */}
                          {isExpanded && (
                            <div className="border-t border-slate-200">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-white">
                                    <TableHead className="pl-12">Date</TableHead>
                                    <TableHead>Age</TableHead>
                                    <TableHead>Time in Bed</TableHead>
                                    <TableHead>Sleep Time</TableHead>
                                    <TableHead>Efficiency</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {patient.entries.map((entry, idx) => (
                                    <TableRow key={entry.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                      <TableCell className="pl-12 font-medium">{entry.date}</TableCell>
                                      <TableCell>{entry.age}</TableCell>
                                      <TableCell>{formatDuration(entry.total_time_in_bed_minutes)}</TableCell>
                                      <TableCell>{formatDuration(entry.total_sleep_time_minutes)}</TableCell>
                                      <TableCell className="font-medium">{entry.sleep_efficiency}%</TableCell>
                                      <TableCell>
                                        <Badge className={getClassificationColor(entry.sleep_classification)}>
                                          {entry.sleep_classification}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleViewEntryDetails(entry)}
                                            title="View Details"
                                          >
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDownloadEntry(entry.id, entry.subject_name, entry.date)}
                                            title="Download PDF"
                                          >
                                            <Download className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteClick(entry)}
                                            title="Delete Entry"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg">Registered Patients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Entries</TableHead>
                        <TableHead>Latest Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => u.role === 'user').map(u => {
                        const entryCount = getUserEntryCount(u.id);
                        const latestEntry = getUserLatestEntry(u.id);
                        return (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>
                              <Badge className="bg-slate-100 text-slate-700">Patient</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{entryCount}</span>
                              <span className="text-slate-500">/14</span>
                            </TableCell>
                            <TableCell>
                              {latestEntry ? (
                                <Badge className={getClassificationColor(latestEntry.sleep_classification)}>
                                  {latestEntry.sleep_classification}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">No entries</span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-500">
                              {new Date(u.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewUserEntries(u)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                                {entryCount > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadUserEntries(u.id, u.name)}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    CSV
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {users.filter(u => u.role === 'user').length === 0 && (
                    <div className="text-center py-8 text-slate-500">No patients found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="entries">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg">All Sleep Entries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>Time in Bed</TableHead>
                        <TableHead>Sleep Time</TableHead>
                        <TableHead>Efficiency</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{entry.date}</TableCell>
                          <TableCell>{entry.subject_name}</TableCell>
                          <TableCell>{entry.age}</TableCell>
                          <TableCell>{formatDuration(entry.total_time_in_bed_minutes)}</TableCell>
                          <TableCell>{formatDuration(entry.total_sleep_time_minutes)}</TableCell>
                          <TableCell className="font-medium">{entry.sleep_efficiency}%</TableCell>
                          <TableCell>
                            <Badge className={getClassificationColor(entry.sleep_classification)}>
                              {entry.sleep_classification}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewEntryDetails(entry)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadEntry(entry.id, entry.subject_name, entry.date)}
                                title="Download PDF"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(entry)}
                                title="Delete Entry"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-entry-${entry.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {entries.length === 0 && (
                    <div className="text-center py-8 text-slate-500">No entries found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* User Entries Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold">{selectedUser?.name}'s Sleep Reports</p>
                <p className="text-sm text-slate-500 font-normal">{selectedUser?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingUserEntries ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : userEntries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Moon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No sleep entries recorded for this patient yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-500">Total Entries</p>
                      <p className="text-2xl font-bold text-primary">{userEntries.length}/14</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-500">Avg Efficiency</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {(userEntries.reduce((sum, e) => sum + e.sleep_efficiency, 0) / userEntries.length).toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-500">Good Sleep Days</p>
                      <p className="text-2xl font-bold text-green-600">
                        {userEntries.filter(e => e.sleep_classification === 'Good').length}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownloadUserEntries(selectedUser.id, selectedUser.name)}
                    className="btn-primary"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download All
                  </Button>
                </div>

                <div className="space-y-3">
                  {userEntries.map(entry => (
                    <div 
                      key={entry.id}
                      className="bg-white border border-slate-200 rounded-lg p-4 hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{entry.date}</p>
                            <p className="text-sm text-slate-500">{entry.day_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getClassificationColor(entry.sleep_classification)}>
                            {entry.sleep_classification}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewEntryDetails(entry)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadEntry(entry.id, entry.subject_name, entry.date)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Time in Bed</p>
                          <p className="font-medium">{formatDuration(entry.total_time_in_bed_minutes)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Sleep Time</p>
                          <p className="font-medium">{formatDuration(entry.total_sleep_time_minutes)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Efficiency</p>
                          <p className={`font-medium ${
                            entry.sleep_efficiency >= 90 ? 'text-green-600' :
                            entry.sleep_efficiency >= 85 ? 'text-amber-600' : 'text-red-600'
                          }`}>{entry.sleep_efficiency}%</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Quality</p>
                          <p className="font-medium">{entry.quality}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Details Modal */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-6">
          {selectedEntry && (
            <div className="space-y-6">
              {/* Report Header - Logo on RIGHT, Text on LEFT */}
              <div className="pb-4">
                <div className="flex items-start justify-between">
                  {/* Left side - SleepMed text */}
                  <div>
                    <h1 className="text-3xl font-bold text-[#1e3a5f]">SleepMed</h1>
                    <p className="text-slate-500 text-sm">Laboratory · Solutions · Education</p>
                  </div>
                  {/* Right side - Logo */}
                  <img 
                    src={LOGO_URL} 
                    alt="SleepMed Logo" 
                    className="h-20 w-auto"
                    data-testid="admin-report-logo"
                  />
                </div>
                {/* Blue divider line */}
                <div className="h-1 bg-primary w-full mt-4 mb-4"></div>
                {/* Title centered */}
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-primary">Sleep Diary Report</h2>
                  <p className="text-slate-500 mt-1">Two-Week Sleep Diary Program</p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-primary mb-3">Patient Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-slate-500">Name</p><p className="font-medium">{selectedEntry.subject_name}</p></div>
                  <div><p className="text-slate-500">Age</p><p className="font-medium">{selectedEntry.age} years</p></div>
                  <div><p className="text-slate-500">Date</p><p className="font-medium">{selectedEntry.date}</p></div>
                  <div><p className="text-slate-500">Day Type</p><p className="font-medium capitalize">{selectedEntry.day_type}</p></div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-primary mb-3">Sleep Diary Responses</h4>
                <div className="space-y-2 text-sm">
                  {[
                    ["Bed Time", selectedEntry.bed_time],
                    ["Time to Fall Asleep", selectedEntry.sleep_latency],
                    ["Night Awakenings", selectedEntry.wake_count],
                    ["Awakening Duration", selectedEntry.wake_duration],
                    ["Final Wake Time", selectedEntry.final_wake_time],
                    ["Got Out of Bed", selectedEntry.get_up_time],
                    ["Daytime Nap", selectedEntry.nap],
                    ["Sleep Quality", selectedEntry.quality],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between py-2 border-b border-slate-200 last:border-0">
                      <span className="text-slate-500">{label}</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                <h4 className="font-semibold text-primary mb-3">Sleep Analysis Results</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-500">Total Time in Bed</p>
                    <p className="text-xl font-bold text-slate-900">{formatDuration(selectedEntry.total_time_in_bed_minutes)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-500">Total Sleep Time</p>
                    <p className="text-xl font-bold text-slate-900">{formatDuration(selectedEntry.total_sleep_time_minutes)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-500">Sleep Efficiency</p>
                    <p className={`text-xl font-bold ${
                      selectedEntry.sleep_efficiency >= 90 ? 'text-green-600' :
                      selectedEntry.sleep_efficiency >= 85 ? 'text-amber-600' : 'text-red-600'
                    }`}>{selectedEntry.sleep_efficiency}%</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-500">Classification</p>
                    <p className={`text-xl font-bold ${
                      selectedEntry.sleep_classification === 'Good' ? 'text-green-600' :
                      selectedEntry.sleep_classification === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                    }`}>{selectedEntry.sleep_classification}</p>
                  </div>
                </div>
              </div>

              {/* Classification Legend - Text Only */}
              <div className="mb-4">
                <h4 className="font-bold text-slate-800 text-lg mb-4">Sleep Efficiency Classification:</h4>
                <div className="space-y-3 pl-4">
                  <p className="text-green-600 font-medium">Good: 85% or higher - Healthy sleep efficiency</p>
                  <p className="text-amber-600 font-medium">Moderate: 70-84% - Room for improvement</p>
                  <p className="text-red-700 font-medium">Poor: Below 70% - Consult a sleep specialist</p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-slate-500 py-4">
                <p>Generated by SleepMediary</p>
                <p>Report generated on: {new Date().toLocaleString()}</p>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleDownloadEntry(selectedEntry.id, selectedEntry.subject_name, selectedEntry.date)}
                  className="btn-primary"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!entryToDelete} onOpenChange={() => setEntryToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-red-600">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              Delete Sleep Entry
            </DialogTitle>
            <DialogDescription className="pt-4">
              Are you sure you want to delete this sleep diary entry?
            </DialogDescription>
          </DialogHeader>
          
          {entryToDelete && (
            <div className="bg-slate-50 rounded-lg p-4 my-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Patient</p>
                  <p className="font-medium">{entryToDelete.subject_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">{entryToDelete.date}</p>
                </div>
                <div>
                  <p className="text-slate-500">Sleep Efficiency</p>
                  <p className="font-medium">{entryToDelete.sleep_efficiency}%</p>
                </div>
                <div>
                  <p className="text-slate-500">Classification</p>
                  <Badge className={getClassificationColor(entryToDelete.sleep_classification)}>
                    {entryToDelete.sleep_classification}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-sm text-slate-500">
            This action cannot be undone. The entry will be permanently removed from the database and will no longer appear in analytics or reports.
          </p>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Deleting...
                </div>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Entry
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
